import React, { Suspense, lazy } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import NavBar from './components/NavBar.tsx'
import Footer from './components/Footer.tsx'
import Home from './pages/Home.tsx'
import Login from './pages/Login.tsx'
import Signup from './pages/Signup.tsx'
import Profile from './pages/Profile.tsx'
import Reports from './pages/Reports.tsx'
import SetupReport from './pages/SetupReport.tsx'
import AdminAgencySetup from './pages/AdminAgencySetup.tsx'
import AdminAgencyCompanyAccess from './pages/AdminAgencyCompanyAccess.tsx'
import AdminAgencyUserSignup from './pages/AdminAgencyUserSignup.tsx'
import AdminLlmPrompts from './pages/AdminLlmPrompts.tsx'
import AdminUsers from './pages/AdminUsers.tsx'

import AgencyUserManagement from './pages/AgencyUserManagement.tsx'
import AgencyCompanyManagement from './pages/AgencyCompanyManagement.tsx'
import AcceptInvite from './pages/AcceptInvite.tsx'
import SharedReport from './pages/SharedReport.tsx'
import MetricsReference from './pages/MetricsReference.tsx'
import PrivacyPolicy from './pages/PrivacyPolicy.tsx'
import Terms from './pages/Terms.tsx'
import PrivateRoute from './routes/PrivateRoute.tsx'
import ErrorBoundary from './components/ErrorBoundary.tsx'
import { Toaster } from 'react-hot-toast'

// Lazy load heavy components with Chart.js
const CompaniesOverview = lazy(() => import('./pages/CompaniesOverview.tsx'))
const CompanyDashboard = lazy(() => import('./pages/company/CompanyDashboard.tsx'))

const App: React.FC = () => {
  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <Toaster position="top-right" />
      <NavBar />
      <main className="flex-1">
        <ErrorBoundary>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/invite/:token" element={<AcceptInvite />} />
            <Route path="/shared/report/:shareToken" element={<SharedReport />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<Terms />} />
            <Route
              path="/reports"
              element={
                <PrivateRoute>
                  <Reports />
                </PrivateRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <PrivateRoute>
                  <Profile />
                </PrivateRoute>
              }
            />
            <Route
              path="/companies"
              element={
                <PrivateRoute>
                  <Suspense
                    fallback={
                      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                        <div className="text-center">
                          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                          <p className="text-gray-600">Loading...</p>
                        </div>
                      </div>
                    }
                  >
                    <CompaniesOverview />
                  </Suspense>
                </PrivateRoute>
              }
            />
            <Route
              path="/company/:companyId"
              element={
                <PrivateRoute>
                  <Suspense
                    fallback={
                      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                        <div className="text-center">
                          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                          <p className="text-gray-600">Loading Dashboard...</p>
                        </div>
                      </div>
                    }
                  >
                    <CompanyDashboard />
                  </Suspense>
                </PrivateRoute>
              }
            />
            <Route
              path="/setup-report"
              element={
                <PrivateRoute>
                  <SetupReport />
                </PrivateRoute>
              }
            />
            <Route
              path="/setup-report/:reportId"
              element={
                <PrivateRoute>
                  <SetupReport />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/agency-creation"
              element={
                <PrivateRoute>
                  <AdminAgencySetup />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/agency-company-connection"
              element={
                <PrivateRoute>
                  <AdminAgencyCompanyAccess />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <PrivateRoute>
                  <AdminUsers />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/agency-user-signup"
              element={
                <PrivateRoute>
                  <AdminAgencyUserSignup />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/llm-prompts"
              element={
                <PrivateRoute>
                  <AdminLlmPrompts />
                </PrivateRoute>
              }
            />
            <Route
              path="/agency/user-management"
              element={
                <PrivateRoute>
                  <AgencyUserManagement />
                </PrivateRoute>
              }
            />
            <Route
              path="/agency/company-management"
              element={
                <PrivateRoute>
                  <AgencyCompanyManagement />
                </PrivateRoute>
              }
            />
            <Route
              path="/metrics"
              element={
                <PrivateRoute>
                  <MetricsReference />
                </PrivateRoute>
              }
            />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </ErrorBoundary>
      </main>
      <Footer />
    </div>
  )
}

export default App
